import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, BookOpen, Clock, Layers, ListChecks, Play, ScrollText } from 'lucide-react';
import { courses, STEP_COUNT, totalLessons, totalMinutes } from '../content';
import { useProgress } from '../hooks/useProgress';
import Icon from '../components/Icon';

const TRACE = [
  { dir: '→', msg: 'INVITE sip:bob@example.com', cls: 'req' },
  { dir: '←', msg: '407 Proxy Auth Required', cls: 'err' },
  { dir: '→', msg: 'INVITE + Proxy-Authorization', cls: 'req' },
  { dir: '←', msg: '100 Trying', cls: 'prov' },
  { dir: '←', msg: '180 Ringing', cls: 'prov' },
  { dir: '←', msg: '200 OK  (SDP answer)', cls: 'ok' },
  { dir: '→', msg: 'ACK', cls: 'req' },
  { dir: '⇄', msg: 'RTP  PCMA/8000  20 ms', cls: 'media' },
  { dir: '→', msg: 'BYE', cls: 'req' },
  { dir: '←', msg: '200 OK', cls: 'ok' },
];

function LiveTrace() {
  const reduce = useReducedMotion();
  const [n, setN] = useState(reduce ? TRACE.length : 4);
  useEffect(() => {
    if (reduce) return undefined;
    const t = setInterval(() => setN((x) => (x >= TRACE.length + 3 ? 1 : x + 1)), 900);
    return () => clearInterval(t);
  }, [reduce]);
  const shown = TRACE.slice(0, Math.min(n, TRACE.length));
  return (
    <div className="trace" aria-label="Animated SIP call trace">
      <div className="trace-head">
        <span className="code-dots" aria-hidden="true"><i /><i /><i /></span>
        <span>sngrep · 203.0.113.10:5060</span>
        <span className="trace-live"><i /> live</span>
      </div>
      <ol className="trace-lines">
        {shown.map((l, i) => (
          <motion.li
            key={`${i}-${l.msg}`}
            className={`trace-${l.cls}`}
            initial={{ opacity: 0, x: l.dir === '←' ? 24 : -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          >
            <span className="trace-time">{`00:0${Math.floor(i / 2)}.${String((i * 137) % 1000).padStart(3, '0')}`}</span>
            <span className="trace-dir">{l.dir}</span>
            <span className="trace-msg">{l.msg}</span>
          </motion.li>
        ))}
      </ol>
    </div>
  );
}

const FLOATERS = ['REGISTER', 'INVITE', '200 OK', 'ACK', 'BYE', 't_relay()', '$ru', '180 Ringing', 'loose_route()'];

export default function Home() {
  const { courseDone } = useProgress();
  const reduce = useReducedMotion();
  const firstOpen = courses.find((c) => courseDone(c) < c.lessons.length) || courses[0];

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-floaters" aria-hidden="true">
          {FLOATERS.map((f, i) => (
            <motion.span
              key={f}
              className="floater"
              style={{ left: `${(i * 11 + 4) % 92}%`, top: `${(i * 23 + 8) % 85}%` }}
              animate={reduce ? {} : { y: [0, -14, 0], rotate: [0, i % 2 ? 4 : -4, 0] }}
              transition={{ duration: 5 + (i % 3), repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
            >
              {f}
            </motion.span>
          ))}
        </div>
        <div className="hero-grid">
          <div className="hero-copy">
            <motion.p className="eyebrow" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              Free course · SIP · Kamailio
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
              Learn <span className="grad-text">Kamailio</span> and <span className="grad-text alt">SIP</span> in{' '}
              <span className="hero-num">{STEP_COUNT}</span> Steps
            </motion.h1>
            <motion.p className="lede" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}>
              Start with how a phone call is set up in SIP. Finish with a Kamailio server that registers users, blocks attackers,
              relays media through NAT and balances calls across a PBX farm.
            </motion.p>
            <motion.div className="hero-cta" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}>
              <Link className="btn btn-primary btn-lg" to={`/course/${firstOpen.id}`}>
                <Play size={18} /> {firstOpen.step === 1 && courseDone(firstOpen) === 0 ? 'Start step 1' : `Continue step ${firstOpen.step}`}
              </Link>
              <Link className="btn btn-ghost btn-lg" to="/courses"><BookOpen size={18} /> Browse courses</Link>
            </motion.div>
            <ul className="hero-stats">
              <li><Layers size={18} /><b>{STEP_COUNT}</b> courses</li>
              <li><ListChecks size={18} /><b>{totalLessons}</b> lessons</li>
              <li><Clock size={18} /><b>{Math.round(totalMinutes / 60)} h</b> of reading</li>
            </ul>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.94, rotate: -2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ delay: 0.2, type: 'spring' }}>
            <LiveTrace />
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <p className="eyebrow">The roadmap</p>
          <h2>{STEP_COUNT} steps, from protocol to production</h2>
          <p>Each step is a separate course with its own lessons and a quiz. Take them in order, or jump to the one you need.</p>
        </div>
        <ol className="roadmap">
          {courses.map((c, i) => {
            const done = courseDone(c);
            return (
              <motion.li
                key={c.id}
                style={{ '--c1': c.colors[0], '--c2': c.colors[1] }}
                initial={{ opacity: 0, x: i % 2 ? 40 : -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: 'spring', stiffness: 120, damping: 18, delay: 0.3 + i * 0.07 }}
              >
                <span className="roadmap-node">{c.step}</span>
                <Link to={`/course/${c.id}`} className="roadmap-card">
                  <span className="roadmap-icon"><Icon name={c.icon} size={26} /></span>
                  <div className="roadmap-body">
                    <span className="roadmap-meta">Step {c.step} · {c.level} · {c.lessons.length} lessons</span>
                    <h3>{c.title}</h3>
                    <p>{c.tagline}</p>
                    <div className="bar"><span style={{ width: `${(done / c.lessons.length) * 100}%` }} /></div>
                  </div>
                  <ArrowRight className="roadmap-go" size={22} />
                </Link>
              </motion.li>
            );
          })}
        </ol>
      </section>

      <section className="section">
        <div className="section-head">
          <p className="eyebrow">By the end</p>
          <h2>What you will be able to build</h2>
        </div>
        <div className="builds">
          {[
            { icon: 'BookUser', title: 'A secure registrar', text: 'Digest auth against MySQL, flood bans and an anti-relay policy.', step: 6 },
            { icon: 'Scale', title: 'A PBX load balancer', text: 'Dispatcher with health probes and automatic failover.', step: 7 },
            { icon: 'Router', title: 'A NAT-proof edge', text: 'nathelper and RTPEngine so remote phones always have audio.', step: 7 },
            { icon: 'Globe', title: 'A WebRTC gateway', text: 'Browsers on secure WebSockets talking to desk phones.', step: 7 },
          ].map((b, i) => (
            <motion.div className="build" key={b.title} whileHover={{ y: -6, rotate: i % 2 ? 0.6 : -0.6 }}>
              <span className="build-icon"><Icon name={b.icon} size={24} /></span>
              <h3>{b.title}</h3>
              <p>{b.text}</p>
              <span className="build-step">Step {b.step}</span>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section">
        <Link to="/cheatsheet" className="cheat-cta">
          <ScrollText size={30} />
          <div>
            <h3>Keep the cheat sheet open</h3>
            <p>Pseudo-variables, core functions, kamcmd commands and SIP response codes on one page.</p>
          </div>
          <ArrowRight size={22} />
        </Link>
      </section>
    </div>
  );
}
